import { useCallback } from 'react';

import axios from 'axios';

import noop from 'lodash/noop';

import settings from '@src/conf';
import { getTimezone } from '@src/corelib/utils/dateUtils';
import { is401 } from '@src/corelib/utils/httpUtils';

function createApiClient() {
  const instance = axios.create(settings.CLIENTS.origin);
  instance.defaults.withCredentials = true;
  instance.defaults.headers.common['Content-Type'] = 'application/json';
  instance.defaults.paramsSerializer = {
    serialize: (params) => {
      const usp = new URLSearchParams();
      Object.entries(params || {}).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          return; 
        }
        if (Array.isArray(value)) {
          value.forEach((v) => usp.append(key, String(v)));
        }
        else if (typeof value === 'object') {
          usp.append(key, JSON.stringify(value));
        }
        else {
          usp.append(key, String(value));
        }
      });
      return usp.toString();
    },
  };

  return instance;
}

const instances = {
  auth: null,
  noauth: null,
};
export function useAPIClientBase(params) {
  const { auth, on401 = noop } = params || {};

  if (!instances.noauth) {
    instances.noauth = createApiClient();
  }

  if (!instances.auth) {
    instances.auth = createApiClient();
    instances.auth.interceptors.response.use(undefined, (error) => {
      if (is401(error.response)) {
        const { refresh = null } = auth.credentials || {};

        if (!refresh) {
          auth.unauthenticate();
          on401();
          return null;
        }

        return auth.refreshToken().then((credentials) => {
          error.config.headers.Authorization = `Bearer ${credentials.access}`;
          return instances.auth.request(error.config);
        })
          .catch(() => {
            auth.unauthenticate();
            on401();
            return null;
          });
      }

      return Promise.reject(error);
    });
  }

  const request = useCallback(
    ({
      url = '',
      method = 'GET',
      payload = null,
      contentType = 'application/json',
      useAuth = true,
      headers = {},
      options = {},
      bodyOnDelete = false,
    }) => {
      let jsonOrFormData = payload;
      let instance = null;
      const paramMethods = ['GET', ...(bodyOnDelete ? [] : ['DELETE'])];
      const dataOrParams = paramMethods.includes(method)
        ? 'params'
        : 'data';

      if (useAuth) {
        // Set instances.auth as base
        instance = instances.auth;
        // Get access token from auth state
        const { access = null } = auth.credentials || {};
        // Access token not founded
        if (!access) {
          auth.unauthenticate();
          on401();
          return Promise.reject(new Error('AccessToken not found.'));
        }

        // Access token founded
        instance.defaults.headers.common.Authorization = `Bearer ${access}`;
      }
      else {
        // Set instances.noauth as base
        instance = instances.noauth;
      }

      instance.defaults.headers.common['Content-Type'] = contentType;
      instance.defaults.headers.common['User-Timezone'] = getTimezone();

      return new Promise((resolve, reject) => {
        instance
          .request({
            ...options,
            url,
            method,
            headers,
            [dataOrParams]: jsonOrFormData,
          })
          .then((response) => {
            const { data, ...params } = response;
            resolve(data, params);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    [auth, on401],
  );

  return { request };
}
